import { useEffect, useRef, useState } from "react";
import WhatsAppIcon from "../icons/WhatsAppIcon";
import { enlaceWhatsApp, whatsappDisponible } from "../../lib/contacto";
import { registrarClicContacto } from "../../lib/contactoClic";

// Botón flotante de WhatsApp de la web pública (2026-10-30). Sebastián lo
// pidió "siempre que no tape botones, avisos ni contenido en móvil", así que
// no se fía de una posición fija: se MUESTRA solo cuando lo que tiene debajo
// es fondo vacío.
//   - Mientras la página se desplaza, se aparta (sin parpadear sobre el
//     contenido que pasa por debajo) y vuelve al quedar quieta.
//   - Al quedar quieta mira qué hay bajo cada esquina y el centro del botón
//     (elementsFromPoint). Si hay un enlace, un botón, un campo, una imagen o
//     texto, o un aviso fijo de la app, no aparece.
//   - Con un campo enfocado (el teclado del teléfono abierto) no aparece.
//   - Cerca del pie no aparece: el pie ya trae WhatsApp y el correo.
// Lo monta App solo en la web, sin sesión y en las páginas públicas. Es
// discreto a propósito: gris de la tinta sobre el papel, como las redes del
// pie, sin el verde de la marca. Todo bajo prefers-reduced-motion.

const INTERACTIVO = 'a,button,input,select,textarea,label,summary,[role="button"],[role="link"],[role="dialog"],[tabindex]:not([tabindex="-1"])';
const CONTENIDO = new Set(["IMG", "SVG", "PATH", "VIDEO", "CANVAS", "IFRAME", "P", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "SPAN", "STRONG", "EM", "DT", "DD", "TD", "TH", "BLOCKQUOTE", "FIGCAPTION", "CODE", "PRE"]);

// ¿El elemento visible en ese punto es contenido (algo que leer o tocar)? Lo
// que no se reconoce como fondo vacío cuenta como contenido: ante la duda, el
// botón no aparece.
export function esContenido(el) {
  if (!el || el === document.documentElement || el === document.body) return false;
  if (el.closest?.(INTERACTIVO)) return true;
  if (CONTENIDO.has(String(el.tagName).toUpperCase())) return true;
  for (const n of el.childNodes || []) {
    if (n.nodeType === 3 && n.textContent.trim()) return true;
  }
  // Un aviso o una hoja fija de la app (toast, banner de actualización):
  // aunque su punto caiga en el relleno, está tapando algo que importa.
  try {
    for (let a = el; a && a !== document.body; a = a.parentElement) {
      const pos = window.getComputedStyle(a).position;
      if (pos === "fixed" || pos === "sticky") return true;
    }
  } catch { /* sin estilos computados: se sigue con lo de arriba */ }
  return false;
}

// Todo lo que se lee o se toca, aunque todavía no se vea: el chat de ejemplo
// de Benito reserva su texto invisible antes de escribirlo, y un bloque que
// entra al bajar parte transparente. Si su caja se cruza con el botón, el
// botón no aparece.
const SELECTOR_CONTENIDO = 'a,button,input,select,textarea,label,summary,img,svg,video,canvas,iframe,p,h1,h2,h3,h4,h5,h6,li,dt,dd,td,th,blockquote,figcaption,pre,[role="button"],[role="dialog"],[role="status"],[role="alert"]';

const seCruzan = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

// ¿La parte del elemento que cae bajo el botón queda recortada por un
// ancestro con overflow? Entonces no se ve ahí y no cuenta.
function recortadoFuera(el, rb) {
  let r = el.getBoundingClientRect();
  for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
    let cs;
    try { cs = window.getComputedStyle(a); } catch { return false; }
    if ([cs.overflow, cs.overflowX, cs.overflowY].some((v) => v && v !== "visible")) {
      const ra = a.getBoundingClientRect();
      r = { left: Math.max(r.left, ra.left), top: Math.max(r.top, ra.top), right: Math.min(r.right, ra.right), bottom: Math.min(r.bottom, ra.bottom) };
      if (!seCruzan(r, rb)) return true;
    }
  }
  return false;
}

// Lo que está bajo el botón, sin contar el botón mismo. Dos miradas:
//   1. Geometría: cualquier control, texto o imagen cuya caja se cruce con el
//      botón (salvo que esté recortado justo ahí).
//   2. Superficie (elementsFromPoint en una grilla de 5×5): lo de más arriba
//      en cada punto, para cazar un aviso fijo o un texto suelto en un div.
// Ante la duda (no se puede medir), hay algo debajo.
export function hayAlgoDebajo(boton) {
  if (!boton || typeof document.elementsFromPoint !== "function") return true;
  const rb = boton.getBoundingClientRect();
  if (!rb.width || !rb.height) return true;

  for (const el of document.querySelectorAll(SELECTOR_CONTENIDO)) {
    if (boton.contains(el) || el.contains(boton)) continue;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height || !seCruzan(r, rb)) continue;
    if (recortadoFuera(el, rb)) continue;
    return true;
  }

  const m = 1;
  const paso = (a, b, i) => a + m + ((b - a - 2 * m) * i) / 4;
  for (let i = 0; i <= 4; i += 1) {
    for (let j = 0; j <= 4; j += 1) {
      const pila = document.elementsFromPoint(paso(rb.left, rb.right, i), paso(rb.top, rb.bottom, j));
      const visible = pila.find((el) => !boton.contains(el));
      if (esContenido(visible)) return true;
    }
  }
  return false;
}

const campoEnfocado = () => {
  const a = typeof document !== "undefined" ? document.activeElement : null;
  if (!a) return false;
  return a.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(a.tagName);
};

export default function WhatsAppFlotante({ idPie = "pie-mipae" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!whatsappDisponible()) return undefined;
    let quieto = null;
    let pieVisible = false;
    // Mientras la página se mueve nadie lo muestra (ni un cambio del DOM ni la
    // mirada periódica): solo decide al quedar quieta.
    let moviendo = false;
    const evaluar = () => {
      const b = ref.current;
      if (!b || moviendo) return;
      setVisible(!pieVisible && !campoEnfocado() && !hayAlgoDebajo(b));
    };
    // Al moverse, se aparta; al quedarse quieta la página, decide.
    const alMover = () => {
      moviendo = true;
      setVisible(false);
      clearTimeout(quieto);
      quieto = setTimeout(() => { moviendo = false; evaluar(); }, 220);
    };
    let io = null;
    const pie = document.getElementById(idPie);
    if (pie && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(([e]) => { pieVisible = e.isIntersecting; alMover(); }, { rootMargin: "0px 0px 80px 0px" });
      io.observe(pie);
    }
    window.addEventListener("scroll", alMover, { passive: true });
    window.addEventListener("resize", alMover);
    document.addEventListener("focusin", alMover);
    document.addEventListener("focusout", alMover);
    // Lo de abajo también cambia sin moverse: el chat de ejemplo de Benito se
    // escribe solo y crece, se abre un aviso, carga una imagen. Cada cambio
    // del documento vuelve a mirar (una vez por cuadro), y además una mirada
    // cada poco tiempo por si algo se movió sin cambiar el DOM.
    let cuadro = null;
    const alCambiar = () => {
      if (cuadro) return;
      cuadro = window.requestAnimationFrame(() => { cuadro = null; evaluar(); });
    };
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(alCambiar) : null;
    mo?.observe(document.body, { childList: true, subtree: true, characterData: true });
    const cada = setInterval(evaluar, 1000);
    quieto = setTimeout(evaluar, 600);
    return () => {
      clearTimeout(quieto);
      clearInterval(cada);
      if (cuadro) window.cancelAnimationFrame(cuadro);
      mo?.disconnect();
      io?.disconnect();
      window.removeEventListener("scroll", alMover);
      window.removeEventListener("resize", alMover);
      document.removeEventListener("focusin", alMover);
      document.removeEventListener("focusout", alMover);
    };
  }, [idPie]);

  if (!whatsappDisponible()) return null;
  // Escondido sigue en su lugar (para poder medir lo que tiene debajo), pero
  // sin poder tocarse ni leerse: no queda un control invisible en el camino.
  return (
    <a
      ref={ref}
      href={enlaceWhatsApp("general")}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Escribir a Sebastián por WhatsApp"
      title="Escribir a Sebastián por WhatsApp"
      aria-hidden={visible ? undefined : "true"}
      tabIndex={visible ? undefined : -1}
      data-visible={visible ? "si" : "no"}
      onClick={() => registrarClicContacto("whatsapp", "flotante")}
      className={[
        "whatsapp-flotante fixed right-4 z-30 grid h-12 w-12 place-items-center rounded-full",
        "border border-edge bg-paper text-ink-muted shadow-tk-modal hover:text-ink",
        "transition-opacity duration-200 motion-reduce:transition-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
        visible ? "opacity-100" : "pointer-events-none opacity-0",
      ].join(" ")}
      style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <WhatsAppIcon className="h-6 w-6" />
    </a>
  );
}
